import React, { useEffect, useMemo, useState } from 'react'
import { Gem, Loader, XCircle, Package, Edit3 } from 'lucide-react'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import kztApi from '../services/kztApi'
import { closePreparedPaymentWindow, prepareExternalPaymentWindow } from '../utils/openExternalPaymentLink'

/**
 * KztPayment
 * Оплата в тенге (KZT) для Казахстана
 * Поддерживает два режима: готовые пакеты и произвольная сумма (KZT/кристаллы)
 */
const KztPayment = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()

  // Состояния для табов
  const [activeTab, setActiveTab] = useState('packages') // packages | custom

  // Конфигурация kzt1 (курсы и лимиты)
  const [kztConfig, setKztConfig] = useState(null)
  const [isLoadingConfig, setIsLoadingConfig] = useState(true)
  const [configError, setConfigError] = useState(null)

  // Пакеты
  const [packages, setPackages] = useState([])
  const [isLoadingPackages, setIsLoadingPackages] = useState(true)
  const [packagesError, setPackagesError] = useState(null)

  // Произвольная сумма
  const [kztInput, setKztInput] = useState('')
  const [crystalsInput, setCrystalsInput] = useState('')
  const [customAmountError, setCustomAmountError] = useState('')

  // Обработка платежа
  const [isProcessing, setIsProcessing] = useState(false)
  const [processingPackageId, setProcessingPackageId] = useState(null)

  useEffect(() => {
    loadKztConfig()
  }, [])

  useEffect(() => {
    loadPackages()
  }, [])

  const rates = useMemo(() => {
    const priceOneCrystalInKzt = parseFloat(kztConfig?.price_one_crystal_in_kzt || '0')
    const priceOneKztInCrystal = parseFloat(kztConfig?.price_one_kzt_in_crystal || '0')

    return {
      priceOneCrystalInKzt,
      priceOneKztInCrystal
    }
  }, [kztConfig])

  // Форматирование чисел с пробелами для отображения
  const formatNumber = (num) => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
  }

  const loadKztConfig = async () => {
    setIsLoadingConfig(true)
    setConfigError(null)

    try {
      const config = await kztApi.fetchKztConfig()
      setKztConfig(config)
      console.log('✅ KZT config loaded:', config)
    } catch (error) {
      console.error('❌ Error loading KZT config:', error)
      setConfigError(t('payment.sbp.configError'))
    } finally {
      setIsLoadingConfig(false)
    }
  }

  const loadPackages = async () => {
    setIsLoadingPackages(true)
    setPackagesError(null)

    try {
      const kztPackages = await kztApi.fetchKztPackages()

      // Сортируем пакеты: самый дешевый первый, затем популярные
      const sortedPackages = [...kztPackages].sort((a, b) => {
        const minPrice = Math.min(...kztPackages.map((p) => p.kzt1))

        if (a.kzt1 === minPrice && b.kzt1 !== minPrice) return -1
        if (b.kzt1 === minPrice && a.kzt1 !== minPrice) return 1

        if (a.popular && !b.popular) return -1
        if (b.popular && !a.popular) return 1

        return a.kzt1 - b.kzt1
      })

      setPackages(sortedPackages)
      console.log('✅ KZT packages loaded:', sortedPackages)
    } catch (error) {
      console.error('❌ Error loading KZT packages:', error)
      setPackagesError(t('payment.sbp.packagesError'))
    } finally {
      setIsLoadingPackages(false)
    }
  }

  const openPaymentLink = (paymentLink, paymentWindow = null) => {
    kztApi.openKztPayment(paymentLink, () => {
      toast.success(t('payment.sbp.paymentNotification'), {
        duration: 4000,
        icon: '💳'
      })

      setTimeout(() => {
        navigate('/')
      }, 1500)
    }, paymentWindow)
  }

  const handlePackagePayment = async (pkg) => {
    if (isProcessing) return

    const paymentWindow = prepareExternalPaymentWindow()

    setIsProcessing(true)
    setProcessingPackageId(pkg.id)

    try {
      console.log('💰 Создание платежа KZT для пакета:', pkg)

      // Для пакетов отправляем только сумму в KZT
      const { link } = await kztApi.createPaymentByKzt(pkg.kzt1)
      openPaymentLink(link, paymentWindow)
    } catch (error) {
      closePreparedPaymentWindow(paymentWindow)
      console.error('❌ Ошибка создания платежа KZT:', error)

      if (error.message === 'INVALID_AMOUNT') {
        toast.error(
          t('payment.kzt.errorInvalidAmount', {
            min: kztConfig?.min_kzt ?? 1000,
            max: kztConfig?.max_kzt ?? 900000
          })
        )
      } else if (error.message === 'BACKEND_ERROR') {
        toast.error(t('payment.sbp.errorBackend'))
      } else if (error.message === 'LIMIT_ORDER') {
        toast.error(t('payment.kzt.errorLimitOrder'))
      } else {
        toast.error(t('payment.createError'))
      }
    } finally {
      setIsProcessing(false)
      setProcessingPackageId(null)
    }
  }

  const handleKztChange = (e) => {
    const value = e.target.value.replace(/[^\d]/g, '')
    setKztInput(value)
    setCustomAmountError('')

    if (value && rates.priceOneKztInCrystal) {
      const kzt = parseFloat(value)
      if (Number.isFinite(kzt)) {
        const crystals = Math.round(kzt * rates.priceOneKztInCrystal)
        setCrystalsInput(crystals.toString())
      } else {
        setCrystalsInput('')
      }
    } else {
      setCrystalsInput('')
    }
  }

  const handleCrystalsChange = (e) => {
    const value = e.target.value.replace(/[^\d]/g, '')
    setCrystalsInput(value)
    setCustomAmountError('')

    if (value && rates.priceOneCrystalInKzt) {
      const crystals = parseFloat(value)
      if (Number.isFinite(crystals)) {
        const kzt = Math.round(crystals * rates.priceOneCrystalInKzt)
        setKztInput(kzt.toString())
      } else {
        setKztInput('')
      }
    } else {
      setKztInput('')
    }
  }

  const validateCustomAmount = () => {
    if (!kztInput || !kztConfig) {
      setCustomAmountError(t('payment.sbp.enterAmount'))
      return false
    }

    const kzt = parseFloat(kztInput)
    if (!Number.isFinite(kzt)) {
      setCustomAmountError(t('payment.crypto.enterCorrectAmount'))
      return false
    }

    if (kzt < kztConfig.min_kzt || kzt > kztConfig.max_kzt) {
      setCustomAmountError(
        t('payment.kzt.errorInvalidAmount', {
          min: formatNumber(kztConfig.min_kzt),
          max: formatNumber(kztConfig.max_kzt)
        })
      )
      return false
    }

    return true
  }

  const handleCustomPayment = async () => {
    if (!validateCustomAmount() || isProcessing) return

    const paymentWindow = prepareExternalPaymentWindow()

    setIsProcessing(true)

    try {
      const kzt = parseInt(kztInput || '0', 10)
      console.log('💰 Создание платежа KZT (custom):', { kzt })

      // Для произвольной суммы всегда отправляем только kzt
      const payment = await kztApi.createPaymentByKzt(kzt)
      openPaymentLink(payment.link, paymentWindow)
    } catch (error) {
      closePreparedPaymentWindow(paymentWindow)
      console.error('❌ Ошибка создания платежа KZT:', error)

      if (error.message === 'INVALID_AMOUNT') {
        setCustomAmountError(
          t('payment.kzt.errorInvalidAmount', {
            min: formatNumber(kztConfig?.min_kzt ?? 1000),
            max: formatNumber(kztConfig?.max_kzt ?? 900000)
          })
        )
      } else if (error.message === 'BACKEND_ERROR') {
        toast.error(t('payment.sbp.errorBackend'))
      } else if (error.message === 'LIMIT_ORDER') {
        toast.error(t('payment.kzt.errorLimitOrder'))
      } else {
        toast.error(t('payment.createError'))
      }
    } finally {
      setIsProcessing(false)
    }
  }

  const renderPackages = () => {
    if (isLoadingPackages) {
      return (
        <div className="grid grid-cols-1 gap-4">
          {[...Array(5)].map((_, index) => (
            <div
              key={index}
              className="bg-surface rounded-lg border border-white border-opacity-10 p-4 animate-pulse"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gray-600 rounded-lg"></div>
                  <div>
                    <div className="h-5 bg-gray-600 rounded w-20 mb-2"></div>
                    <div className="h-4 bg-gray-600 rounded w-16"></div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="h-5 bg-gray-600 rounded w-20 mb-2"></div>
                  <div className="h-6 bg-gray-600 rounded w-16"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )
    }

    if (packagesError) {
      return (
        <div className="text-center py-8">
          <XCircle size={48} className="text-red-400 mx-auto mb-4" />
          <p className="text-red-400 mb-4">{packagesError}</p>
          <button
            onClick={loadPackages}
            className="px-6 py-3 bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary font-semibold rounded-lg hover:opacity-90 transition-opacity"
          >
            {t('common.retry')}
          </button>
        </div>
      )
    }

    return (
      <div className="grid grid-cols-1 gap-4">
        {packages.map((pkg) => (
          <div
            key={pkg.id}
            className={`relative bg-surface rounded-lg border border-white border-opacity-10 p-4 cursor-pointer transition-all hover:border-accent-primary/50 hover:scale-[1.02] active:scale-[0.98] ${
              pkg.popular ? 'ring-2 ring-accent-primary ring-opacity-50' : ''
            } ${
              isProcessing && processingPackageId === pkg.id
                ? 'opacity-50 cursor-not-allowed'
                : ''
            }`}
            onClick={() => !isProcessing && handlePackagePayment(pkg)}
          >
            {pkg.popular && (
              <div className="absolute -top-2 -right-2">
                <span className="inline-block px-3 py-1 bg-accent-primary text-dark-primary text-xs font-bold rounded-full shadow-lg">
                  {t('payment.popular')}
                </span>
              </div>
            )}

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 bg-gradient-to-br from-accent-primary to-accent-secondary rounded-xl flex items-center justify-center flex-shrink-0">
                  <Gem size={28} className="text-dark-primary" />
                </div>
                <div>
                  <p className="text-white font-bold text-xl">{formatNumber(pkg.crystals)}</p>
                  <p className="text-text-secondary text-sm">{t('payment.crypto.crystals')}</p>
                  {/* Бонусные кристаллы от промокода */}
                  {pkg.promo_bonus_crystals > 0 && (
                    <p className="text-accent-primary text-xs font-semibold mt-0.5">
                      +{formatNumber(pkg.promo_bonus_crystals)} {t('promo.bonus')}
                    </p>
                  )}
                </div>
              </div>

              <div className="text-right">
                {isProcessing && processingPackageId === pkg.id ? (
                  <div className="flex items-center gap-2 text-accent-primary">
                    <Loader size={20} className="animate-spin" />
                  </div>
                ) : (
                  <div className="text-accent-primary font-bold text-lg">
                    <span>{formatNumber(pkg.kzt1)} KZT</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    )
  }

  const renderCustomAmount = () => {
    if (isLoadingConfig) {
      return (
        <div className="text-center py-8">
          <Loader size={48} className="text-accent-primary mx-auto mb-4 animate-spin" />
          <p className="text-text-secondary">{t('payment.sbp.loadingConfig')}</p>
        </div>
      )
    }

    if (configError) {
      return (
        <div className="text-center py-8">
          <XCircle size={48} className="text-red-400 mx-auto mb-4" />
          <p className="text-red-400 mb-4">{configError}</p>
          <button
            onClick={loadKztConfig}
            className="px-6 py-3 bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary font-semibold rounded-lg hover:opacity-90 transition-opacity"
          >
            {t('common.retry')}
          </button>
        </div>
      )
    }

    const hasError = customAmountError !== ''
    const kzt = parseFloat(kztInput)
    const isValidAmount =
      kztInput &&
      !hasError &&
      Number.isFinite(kzt) &&
      kzt >= kztConfig.min_kzt &&
      kzt <= kztConfig.max_kzt

    return (
      <div className="space-y-6">
        <div className="text-center">
          <p className="text-text-secondary text-sm">
            {t('payment.kzt.limits', {
              min: formatNumber(kztConfig.min_kzt),
              max: formatNumber(kztConfig.max_kzt)
            })}
          </p>
        </div>

        <div>
          <label className="block text-sm font-semibold text-white mb-2">{t('payment.kzt.amountKztLabel')}</label>
          <input
            type="text"
            inputMode="numeric"
            value={kztInput}
            onChange={handleKztChange}
            placeholder={t('payment.sbp.enterAmount')}
            className={`w-full px-4 py-3 bg-surface rounded-lg border ${
              hasError ? 'border-red-500' : 'border-white border-opacity-10'
            } text-white text-lg font-semibold focus:outline-none focus:border-accent-primary transition-colors`}
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-white mb-2">{t('payment.crypto.crystals')}</label>
          <input
            type="text"
            inputMode="numeric"
            value={crystalsInput}
            onChange={handleCrystalsChange}
            placeholder={t('payment.sbp.enterCrystals')}
            className={`w-full px-4 py-3 bg-surface rounded-lg border ${
              hasError ? 'border-red-500' : 'border-white border-opacity-10'
            } text-white text-lg font-semibold focus:outline-none focus:border-accent-primary transition-colors`}
          />
        </div>

        {/* Промо-бонус для произвольной суммы */}
        {kztConfig?.promo?.percent && crystalsInput && (
          <div className="bg-accent-primary/10 border border-accent-primary/30 rounded-lg p-3 text-center">
            <span className="text-accent-primary font-semibold text-sm">
              +{formatNumber(Math.ceil(parseInt(crystalsInput) * kztConfig.promo.percent / 100))} {t('promo.bonus')}
            </span>
          </div>
        )}

        {hasError && (
          <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-3">
            <p className="text-red-400 text-sm text-center">{customAmountError}</p>
          </div>
        )}

        <button
          onClick={handleCustomPayment}
          disabled={!isValidAmount || isProcessing}
          className={`w-full py-4 rounded-lg font-bold text-lg transition-all ${
            isValidAmount && !isProcessing
              ? 'bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary hover:opacity-90'
              : 'bg-gray-600 text-gray-400 cursor-not-allowed'
          }`}
        >
          {isProcessing ? (
            <span className="flex items-center justify-center gap-2">
              <Loader size={20} className="animate-spin" />
              {t('payment.kzt.creating')}
            </span>
          ) : (
            `${t('payment.sbp.pay')} ${kztInput ? formatNumber(kztInput) : '0'} KZT`
          )}
        </button>
      </div>
    )
  }

  return (
    <div className="max-w-md mx-auto">
      <div className="mb-6">
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => setActiveTab('packages')}
            className={`
              px-4 py-3 rounded-lg font-semibold transition-all
              ${
                activeTab === 'packages'
                  ? 'bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary'
                  : 'bg-surface text-text-secondary hover:text-white border border-white border-opacity-10'
              }
            `}
          >
            <Package size={18} className="inline mr-2" />
            {t('payment.crypto.tabPackages')}
          </button>

          <button
            onClick={() => setActiveTab('custom')}
            className={`
              px-4 py-3 rounded-lg font-semibold transition-all
              ${
                activeTab === 'custom'
                  ? 'bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary'
                  : 'bg-surface text-text-secondary hover:text-white border border-white border-opacity-10'
              }
            `}
          >
            <Edit3 size={18} className="inline mr-2" />
            {t('payment.crypto.tabCustom')}
          </button>
        </div>
      </div>

      {activeTab === 'packages' ? renderPackages() : renderCustomAmount()}
    </div>
  )
}

export default KztPayment
